import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Input, Button, Typography, Space, Alert, Divider } from 'antd';
import { LockOutlined, MailOutlined, MobileOutlined } from '@ant-design/icons';
import { useStores } from '../../stores';

const { Title, Text, Paragraph } = Typography;

const TwoFactorVerify = observer(() => {
  const { authStore } = useStores();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const isVisible = authStore.requires2FA;
  const method = authStore.twoFAMethod;

  useEffect(() => {
    // Auto-send email code if method is EMAIL
    if (isVisible && method === 'EMAIL' && !emailSent) {
      handleSendEmailCode();
    }
  }, [isVisible, method]);

  const handleVerify = async () => {
    if (!code.trim()) return;

    setLoading(true);
    const result = await authStore.verify2FALogin(code);
    setLoading(false);

    if (result.success) {
      setCode('');
      setEmailSent(false);
    }
  };

  const handleSendEmailCode = async () => {
    setEmailSending(true);
    const result = await authStore.send2FAEmailCode();
    setEmailSending(false);
    if (result.success) {
      setEmailSent(true);
    }
  };

  const handleCancel = () => {
    authStore.cancel2FALogin();
    setCode('');
    setEmailSent(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  return (
    <Modal
      open={isVisible}
      onCancel={handleCancel}
      footer={null}
      closable={true}
      maskClosable={false}
      width={420}
      centered
    >
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <LockOutlined style={{ fontSize: 48, color: '#FF9A76', marginBottom: 16 }} />
        <Title level={3} style={{ marginBottom: 8 }}>Two-Factor Authentication</Title>
        <Paragraph type="secondary">
          {method === 'EMAIL'
            ? 'Enter the verification code sent to your email.'
            : 'Enter the code from your authenticator app.'}
        </Paragraph>
      </div>

      {authStore.error && (
        <Alert
          message={authStore.error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => authStore.setError(null)}
        />
      )}

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Input
            size="large"
            placeholder="Enter 6-digit code"
            prefix={method === 'EMAIL' ? <MailOutlined /> : <MobileOutlined />}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9A-Za-z]/g, ''))}
            onKeyPress={handleKeyPress}
            maxLength={8}
            style={{ textAlign: 'center', letterSpacing: 4, fontSize: 18 }}
          />
        </div>

        <Button
          type="primary"
          size="large"
          block
          onClick={handleVerify}
          loading={loading}
          disabled={code.length < 6}
        >
          Verify
        </Button>

        {method === 'EMAIL' && (
          <>
            <Divider style={{ margin: '12px 0' }}>or</Divider>
            <Button
              type="link"
              block
              onClick={handleSendEmailCode}
              loading={emailSending}
              disabled={emailSending}
            >
              {emailSent ? 'Resend code to email' : 'Send code to email'}
            </Button>
          </>
        )}

        <div style={{ textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Lost access? Enter a backup code instead.
          </Text>
        </div>
      </Space>
    </Modal>
  );
});

export default TwoFactorVerify;
